import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CreateRoleRequest, CreateRoleResponse, DeleteRoleResponse, RolesResponse, RoleResponse, AddRemovePermissionRequest, UpdatePermissionsRequest, UpdateRoleRequest } from '../models/role.model';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class RoleService {
  private readonly apiBaseUrl = (environment.apiBaseUrl || 'http://localhost:3000').replace(/\/+$/, '');
  private readonly apiUrl = `${this.apiBaseUrl}/api/roles`;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService
  ) { }

  /**
   * Get roles with pagination, sorting, and filtering (scoped by backend via JWT tenantId).
   * @param page Page number (default: 1)
   * @param limit Items per page (default: 10, max: 100)
   * @param sort Sort field and order (e.g., 'name', '-name', 'createdAt', '-createdAt')
   * @param filters Object with filter parameters (search, status, name_contains, etc.)
   */
  getRoles(
    page: number = 1,
    limit: number = 10,
    sort?: string,
    filters?: { [key: string]: any }
  ): Observable<RolesResponse> {
    const token = this.authService.getToken();
    const headers = new HttpHeaders({ Authorization: token || '' });

    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', Math.min(limit, 100).toString());

    if (sort) {
      params = params.set('sort', sort);
    }

    if (filters) {
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, String(value));
        }
      });
    }

    return this.http.get<RolesResponse>(this.apiUrl, { headers, params });
  }

  /**
   * Create a new role.
   * Non-root callers are scoped by JWT tenantId. Root callers may pass tenantId,
   * or isRoot: true (no tenantId) to create the global Root role.
   */
  createRole(roleData: CreateRoleRequest): Observable<CreateRoleResponse> {
    const token = this.authService.getToken();
    const headers = new HttpHeaders({
      Authorization: token || '',
      'Content-Type': 'application/json'
    });
    return this.http.post<CreateRoleResponse>(this.apiUrl, roleData, { headers });
  }

  /**
   * Update a role's name and/or description (scoped by backend via JWT tenantId).
   * @param roleId The MongoDB _id of the role
   * @param request Request with name and/or description
   */
  updateRole(roleId: string, request: UpdateRoleRequest): Observable<RoleResponse> {
    const token = this.authService.getToken();
    const headers = new HttpHeaders({
      Authorization: token || '',
      'Content-Type': 'application/json'
    });
    return this.http.patch<RoleResponse>(`${this.apiUrl}/${encodeURIComponent(roleId)}`, request, { headers });
  }

  /**
   * Get a specific role by ID (scoped by backend via JWT tenantId).
   * @param roleId The MongoDB _id of the role
   */
  getRoleById(roleId: string): Observable<RoleResponse> {
    const token = this.authService.getToken();
    const headers = new HttpHeaders({ Authorization: token || '' });
    return this.http.get<RoleResponse>(`${this.apiUrl}/${encodeURIComponent(roleId)}`, { headers });
  }

  /**
   * Add or remove a permission from a role (scoped by backend via JWT tenantId).
   * @param roleId The MongoDB _id of the role
   * @param request Request with action ('add' or 'remove') and permissionId
   */
  addRemovePermission(roleId: string, request: AddRemovePermissionRequest): Observable<RoleResponse> {
    const token = this.authService.getToken();
    const headers = new HttpHeaders({
      Authorization: token || '',
      'Content-Type': 'application/json'
    });
    return this.http.patch<RoleResponse>(`${this.apiUrl}/${encodeURIComponent(roleId)}`, request, { headers });
  }

  /**
   * Replace all permissions for a role (bulk update) (scoped by backend via JWT tenantId).
   * @param roleId The MongoDB _id of the role
   * @param request Request with permissionIds array
   */
  updatePermissions(roleId: string, request: UpdatePermissionsRequest): Observable<RoleResponse> {
    const token = this.authService.getToken();
    const headers = new HttpHeaders({
      Authorization: token || '',
      'Content-Type': 'application/json'
    });
    return this.http.put<RoleResponse>(`${this.apiUrl}/${encodeURIComponent(roleId)}/permissions`, request, { headers }).pipe(
      tap(() => {
        if (this.authService.getRoleId() === roleId)
          this.authService.logout();
      })
    );
  }

  /**
   * Delete a role (scoped by backend via JWT tenantId).
   */
  deleteRole(roleId: string): Observable<DeleteRoleResponse> {
    const token = this.authService.getToken();
    const headers = new HttpHeaders({ Authorization: token || '' });
    return this.http.delete<DeleteRoleResponse>(`${this.apiUrl}/${encodeURIComponent(roleId)}`, { headers });
  }
}

